import type { User } from '@/types/userTypes';

type RumPrimitive = string | number | boolean;
type RumProperties = Record<string, RumPrimitive | null | undefined>;

interface RumEvent {
  type: string;
  name: string;
  group?: string;
  properties?: RumProperties;
}

interface RumUserPayload {
  id?: string;
  name?: string;
  tags?: Record<string, RumPrimitive>;
}

interface RumClient {
  sendCustom?: (event: RumEvent) => void;
  setConfig?: (config: Record<string, unknown>) => void;
  getConfig?: () => Record<string, unknown> | undefined;
}

interface RumModule {
  default?: RumClient;
}

const DEFAULT_GROUP = 'tts_workspace';
const SDK_READY_RETRY_LIMIT = 8;
const SDK_READY_RETRY_DELAY = 500;
const RUM_ENABLED = import.meta.env.VITE_APP_ENV === 'prod';

let pendingUser: RumUserPayload | null | undefined;
let retryCount = 0;

const getRumClient = (): RumClient | undefined => {
  if (!RUM_ENABLED) return undefined;
  if (typeof window === 'undefined') return undefined;

  const win = window as Window & {
    RumSDK?: RumModule;
    __rum?: RumClient;
  };

  if (win.RumSDK?.default?.sendCustom || win.RumSDK?.default?.setConfig) {
    return win.RumSDK.default;
  }

  if (win.__rum?.sendCustom || win.__rum?.setConfig) {
    return win.__rum;
  }

  return undefined;
};

const sanitizeProperties = (properties: RumProperties = {}): Record<string, RumPrimitive> => {
  const result: Record<string, RumPrimitive> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (typeof value === 'string') {
      result[key] = value.length > 2000 ? value.slice(0, 2000) : value;
      return;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      result[key] = value;
    }
  });

  return result;
};

const getRouteInfo = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  return {
    route_path: window.location.pathname,
    route_hash: window.location.hash || undefined
  };
};

const scheduleApplyUser = () => {
  if (!RUM_ENABLED) return;
  if (retryCount >= SDK_READY_RETRY_LIMIT) return;

  retryCount += 1;
  window.setTimeout(() => {
    applyRumUser(pendingUser);
  }, SDK_READY_RETRY_DELAY);
};

const applyRumUser = (user: RumUserPayload | null | undefined) => {
  const client = getRumClient();

  if (!client?.setConfig) {
    scheduleApplyUser();
    return;
  }

  const currentConfig = client.getConfig?.() || {};
  const nextUser = user
    ? {
        ...(currentConfig.user as Record<string, unknown> | undefined),
        ...user
      }
    : undefined;

  client.setConfig({
    ...currentConfig,
    user: nextUser
  });
};

export const syncRumUser = (user?: User | null) => {
  if (!RUM_ENABLED) return;

  retryCount = 0;

  if (!user) {
    pendingUser = null;
    applyRumUser(null);
    return;
  }

  const userId = String(user.id || '');
  pendingUser = {
    name: userId || user.username,
    tags: {
      business_user_id: userId,
      username: user.username || '',
      email: user.email || '',
      role: user.role || '',
      status: user.status || ''
    }
  };

  applyRumUser(pendingUser);
};

export const trackRumClick = (
  area: string,
  action: string,
  properties: RumProperties = {},
  group = DEFAULT_GROUP
) => {
  if (!RUM_ENABLED) return;

  const client = getRumClient();

  if (!client?.sendCustom) {
    return;
  }

  client.sendCustom({
    type: 'user_action',
    name: `click.${area}.${action}`,
    group,
    properties: sanitizeProperties({
      area,
      action,
      ...getRouteInfo(),
      ...properties
    })
  });
};
